// Chip group ids, outside the object so each chip id is built from its group's id.
const loanTypeChips = "new-consultation-loan-type";
const pathChips = "new-consultation-path";
const meetingEditPathChips = "prospect-meeting-edit-path";

export const AutomationTestsIds = {
    newClient: 'newClient',
    navigationUserButton: 'navigation-user-button',
    /** Main drawer menu tile for a top-level route (`ListMenu`), keyed by the route path. */
    navigationLink: (routePath: string): string => `navigation-link-${routePath}`,
    calculator: {
        firstStep: {
            loanPurposeSelect: "loan-purpose-select",
            mortgageRealEstateTypeSelect: "mortgage-real-estate-type-select",
            collateralSelect: "collateral-select",
            otherCollateralSelect: "other-collateral-select",
            purposeAsCollateralRadioGroup: "purpose-as-collateral-radio-group",
            marketTypeRadioGroup: "market-type-radio-group",
            firstOrSubsequentRealEstateRadioGroup: "first-or-subsequent-real-estate-radio-group",
            constructionPeriodInput: "construction-period-input",
            hypothecValueInput: "hypothec-value-input",
            loanAmountInput: "loan-amount-input",
            selfContributionAmountInput: "self-contribution-amount-input",
            selfContributionPercentageInput: "self-contribution-percentage-input",
            currencySelect: "currency-select",
            installmentTypeRadioGroup: "installment-type-radio-group",
            bridgingInsuranceInput: "bridging-insurance-input",
            loanPeriodInput: "loan-period-input",
            loanPeriodSlider: "loan-period-slider"
        },
        secondStep: {
            birthDateInput: "birth-date-input",
            genderRadioGroup: "gender-radio-group",
            civilStatusSelect: "civil-status-select",
            nationalitySelect: "nationality-select",
            residentRadioGroup: "resident-radio-group",
            employmentFormSelect: "employment-form-select"
        },
        navigation: {
            nextButton: "calculator-next-button",
            offersButton: "calculator-offers-button"
        },
        results: {
            offerListing: "simulation-offer-listing",
            offerLoanAmountInput: "offer-loan-amount-input",
            table: {
                expandRow: (rowId: string) => `expand-${rowId}`,
                ltv: {
                    creditNetAmount: 'ltv-credit-net-amount',
                    creditGrossAmount: 'ltv-credit-gross-amount',
                    propertyValueAmount: 'ltv-property-value-amount',
                },
                offersTableEnd: 'offers-table-end',
                offerColumn: 'offer-column',
            },
        }
    },
    /**
     * Shared `AddressForm` component: only the fields the e2e suite drives. The ids are
     * static, so a view that embeds the form gives its instance a wrapper id (e.g.
     * `branches.addBranchModal.addressForm`) and page objects scope these under it —
     * views rendering several address forms (e.g. `OfficialAddressForm`) would otherwise collide.
     */
    addressForm: {
        streetInput: "address-form-street-input",
        streetNumInput: "address-form-street-num-input",
        cityInput: "address-form-city-input",
        zipInput: "address-form-zip-input",
        landSelect: "address-form-land-select",
        districtSelect: "address-form-district-select",
    },
    /** Buttons (add branch, submit, save/undo changes) are located by role + i18n label — no ids. */
    branches: {
        addBranchModal: {
            typeChips: "add-branch-type-chips",
            /** Wrapper of the embedded `AddressForm` — scopes its static field ids to this modal. */
            addressForm: "add-branch-address-form",
            businessCardInput: "add-branch-business-card-input",
            activeCheckbox: "add-branch-active-checkbox",
        },
        list: {
            searchInput: "branches-list-search-input",
            typeSelect: "branches-list-type-select",
            row: "branches-list-row",
            rowNameLink: "branches-list-row-name-link",
            deleteButton: "branches-list-delete-button",
        },
        details: {
            typeChips: "branch-details-type-chips",
            nameInput: "branch-details-name-input",
            activeCheckbox: "branch-details-active-checkbox",
            userAutocomplete: "branch-details-user-autocomplete",
            expertsList: "branch-details-experts-list",
            accessibilityCheckbox: (key: string): string => `branch-accessibility-${key}`,
        },
    },
    confirmModal: {
        falseButton: "confirm-modal-false-button",
        trueButton: "confirm-modal-true-button",
    },
    userManagement: {
        addUserButton: "add-user-button",
        createEditUserModal: {
            firstNameInput: "first-name-input",
            lastNameInput: "last-name-input",
            phoneNumberInput: "phone-number-input",
            emailInput: "email-input",
            peselInput: "pesel-input",
            businessCardInput: "business-card-input",
            newPasswordInput: "new-password-input",
            requirePasswordChangeCheckbox: "require-password-change-checkbox",
            accessesCashCalcCheckbox: "access-cash-calc-checkbox",
            accessesMortgageCalcCheckbox: "access-mortgage-calc-checkbox",
            organisationSelect: "user-modal__organisation-select"
        },
        userBadge: {
            userName: "user-name"
        },
        editUserPanel: {
            firstNameInput: "edit-first-name-input",
            lastNameInput: "edit-last-name-input",
            landSelect: "edit-land-select",
            districtSelect: "edit-district-select",
            saveButton: "edit-save-button"
        },
        userDetails: {
            branchAutocomplete: "user-details-branch-autocomplete"
        }
    },
    modalsShared: {
        cancelButton: "cancel-button",
        saveButton: "save-button"
    },
    activities: {
        todayCard: "activity-tile-today"
    },
    calendar: {
        addActivityButton: "calendar-add-activity-button",
        calendarContainer: "calendar-container",
        activityChip: "calendar-activity-chip",
        activityNameInput: "activity-name-input",
        meetingLocationOffice: "meeting-location-office",
        meetingLocationClient: "meeting-location-client",
        meetingLocationTeleconference: "meeting-location-teleconference",
        startDatePicker: "activity-start-date-picker",
        startTimePicker: "activity-start-time-picker",
        saveActivityButton: "save-activity-button",
        cancelButton: "cancel-activity-button",
        busyScheduleAlert: "busy-schedule-alert",
        deleteActivityButton: "delete-activity-button"
    },
    prospects: {
        exportXlsButton: "prospects-export-xls-button",
        // Root of the prospect details view. Rendered on `prospect && !isClientLoading`, and
        // `fetchProspect` clears that flag at its own end, so its visibility is the proof the
        // prospect arrived — the status itself is asserted from the API response, never from
        // the translated label.
        detailsView: "prospect-details-view",
        editMeetingButton: "prospect-edit-meeting-button",
        meetingSummary: "prospect-meeting-summary",
        meetingEditForm: "prospect-meeting-edit-form",
        meetingEditPathChips,
        meetingEditPathChip: (path: string): string => `${meetingEditPathChips}-${path}`,
        meetingEditExpertSelect: "prospect-meeting-edit-expert-select",
        confirmMeetingEditButton: "prospect-confirm-meeting-edit-button",
        cancelMeetingEditButton: "prospect-cancel-meeting-edit-button"
    },
    // "Nowa konsultacja" modal, opened from the prospects list. Chip groups get a base id
    // and FpChipRadio appends the item key, so a single chip is `<base>-<ENUM_VALUE>` —
    // technical values, never the translated label.
    newConsultation: {
        modal: "new-consultation-modal",
        loanTypeChips,
        loanTypeChip: (loanType: string): string => `${loanTypeChips}-${loanType}`,
        pathChips,
        pathChip: (path: string): string => `${pathChips}-${path}`,
        addressSearchInput: "new-consultation-address-search",
        // The picker the expert path opens with, not the sub-form's own one below.
        expertSelect: "new-consultation-expert-select",
    },
    // NewPassProspectForm.vue, shared by the "Nowa konsultacja" modal and the meeting edit form.
    consultationSubForm: {
        form: "consultation-sub-form",
        consultationKindRadioGroup: "consultation-kind-radio-group",
        availableHours: "consultation-available-hours",
        // Every bookable slot carries this; unavailable ones render without it.
        availableHour: "consultation-available-hour",
        agentSelect: "consultation-agent-select",
        // Marks the note's column, not the field: v-textarea renders its own internals, so the
        // test descends to the textbox by role from here.
        noteInput: "consultation-note-input"
    },
    tablePagination: {
        container: "table-pagination",
        resultsOnPageLabel: "table-pagination-results-on-page-label",
        rowsPerPageSelect: "table-pagination-rows-per-page-select",
        resultsRange: "table-pagination-results-range",
        pageCounter: "table-pagination-page-counter",
        previousPageButton: "table-pagination-previous-page",
        nextPageButton: "table-pagination-next-page"
    },
    fpFilter: {
        filtersChip: "fp-filter-filters-chip"
    },
    fpDataTable: "fp-data-table",
    fpOmnisearch: "fp-omnisearch",
    navigationBar: {
        calendarButton: "navigation-calendar-button",
        mailButton: "navigation-mail-button"
    },
    notification: {
        menuButton: "notification-menu-button"
    },
    dashboard: {
        greeting: "dashboard-greeting",
        /** Rendered for every role — anchors "the dashboard mounted" checks. */
        mainContainer: "dashboard-main-container",
        knowledgeColumn: "dashboard-knowledge-column",
    },
    /** Buttons keep ids: their labels are hardcoded Polish, not i18n keys. */
    knowledgeRepository: {
        page: "knowledge-repository-page",
        filePage: "knowledge-repository-file-page",
        filePageError: "knowledge-repository-file-page-error",
        filePageName: "knowledge-repository-file-page-name",
        filePageDownload: "knowledge-repository-file-page-download",
        searchInput: "knowledge-repository-search-input",
        addFolderButton: "knowledge-repository-add-folder-button",
        addFileButton: "knowledge-repository-add-file-button",
        breadcrumbs: "knowledge-repository-breadcrumbs",
        emptyState: "knowledge-repository-empty-state",
        table: "knowledge-repository-table",
        row: "knowledge-repository-row",
        rowCheckbox: "knowledge-repository-row-checkbox",
        rowActionsButton: "knowledge-repository-row-actions-button",
        readOnlyBadge: "knowledge-repository-read-only-badge",
        createdAtDatepicker: "knowledge-repository-created-at-datepicker",
        menuItem: (action: string): string => `knowledge-repository-menu-item-${action}`,
        bulkActionBar: "knowledge-repository-bulk-action-bar",
        bulkDeleteButton: "knowledge-repository-bulk-delete-button",
        bulkDownloadButton: "knowledge-repository-bulk-download-button",
        bulkMoveButton: "knowledge-repository-bulk-move-button",
        bulkClearButton: "knowledge-repository-bulk-clear-button",
    },
    /**
     * Repozytorium wiedzy (`/filemanager`) — modals and the parts of the view
     * `knowledgeRepository` (above) doesn't cover. Wiersze/menu/checkboxy w listach
     * dynamicznych (foldery, kategorie, grupy widoczności) mają jeden powtarzany id na
     * typ elementu — skalowanie do konkretnego wiersza/elementu odbywa się przez
     * `.filter({ hasText })`, tak jak `branches.list.row` (nazwy plików/folderów/kategorii
     * to dane testowe, nie i18n).
     */
    filemanager: {
        rowName: "filemanager-row-name",
        rowLocationLabel: "filemanager-row-location-label",
        previewPanel: "filemanager-preview-panel",
        previewDownloadButton: "filemanager-preview-download-button",
        previewCloseButton: "filemanager-preview-close-button",
        breadcrumbItem: "filemanager-breadcrumb-item",
        addFolderModal: {
            nameInput: "filemanager-add-folder-name-input",
            submitButton: "filemanager-add-folder-submit-button",
        },
        addFilesModal: {
            dropZone: "filemanager-add-files-drop-zone",
            fileInput: "filemanager-add-files-file-input",
            fileListItem: "filemanager-add-files-list-item",
            selectManyItem: "filemanager-add-files-select-many-item",
            removeFileButton: "filemanager-add-files-remove-button",
            nameInput: "filemanager-add-files-name-input",
            visibilitySelectAllCheckbox: "filemanager-add-files-visibility-select-all",
            visibilityCheckbox: (value: string): string => `filemanager-add-files-visibility-${value}`,
            categoryCheckbox: (key: string): string => `filemanager-add-files-category-${key}`,
            noAccessHint: "filemanager-add-files-no-access-hint",
            submitButton: "filemanager-add-files-submit-button",
        },
        renameModal: {
            nameInput: "filemanager-rename-name-input",
            submitButton: "filemanager-rename-submit-button",
        },
        deleteConfirmModal: {
            findocsWarning: "filemanager-delete-findocs-warning",
            confirmButton: "filemanager-delete-confirm-button",
            cancelButton: "filemanager-delete-cancel-button",
        },
        editAccessModal: {
            selectAllCheckbox: "filemanager-edit-access-select-all",
            visibilityCheckbox: (value: string): string => `filemanager-edit-access-visibility-${value}`,
            noAccessHint: "filemanager-edit-access-no-access-hint",
            saveButton: "filemanager-edit-access-save-button",
        },
        editCategoriesModal: {
            categoryCheckbox: (key: string): string => `filemanager-edit-categories-category-${key}`,
            categoryError: "filemanager-edit-categories-error",
            saveButton: "filemanager-edit-categories-save-button",
        },
        folderTreeSubmenu: {
            navigateUp: "filemanager-folder-tree-navigate-up",
            rootItem: "filemanager-folder-tree-root-item",
            folderItem: "filemanager-folder-tree-folder-item",
            /** The actual `@click="selectFolder(...)"` target — a sibling of the expand button, not the whole row. */
            folderItemContent: "filemanager-folder-tree-folder-item-content",
            expandButton: "filemanager-folder-tree-expand-button",
            confirmButton: "filemanager-folder-tree-confirm-button",
            cancelButton: "filemanager-folder-tree-cancel-button",
        },
        categoryFilter: {
            chip: "filemanager-category-filter-chip",
            selectAllCheckbox: "filemanager-category-filter-select-all",
            categoryCheckbox: (key: string): string => `filemanager-category-filter-category-${key}`,
        },
    },
    simpleDealList: {
        draftsSection: "simple-deal-list-drafts-section",
        draftsHeader: "simple-deal-list-drafts-header",
        draftItem: "simple-deal-list-draft-item",
    },
    clientPreview: {
        lastDocuments: "client-preview-last-documents",
    },
}
